import { useEffect, useRef, useState, type ReactNode } from "react";
import { create } from "zustand";
import { MaskedValue as MaskedValueView, RevealGrantStatus, RevealPasswordDialog, type RevealPasswordStatus } from "@studio";
import { api, ApiError, errorMessage, request } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";

/**
 * 개인정보 가림 표시 (docs/meta-crew-pii-masking-design.md, 요청서 26절 · 시안 0dad238 privacy-reveal).
 * 화면은 시안 공통 부품(MaskedValue · RevealPasswordDialog · RevealGrantStatus), 인증·API 는 여기서 한다.
 * - 서버는 이름·전화·이메일을 늘 가려서 내려준다. 여기서 별표를 계산하지 않는다.
 * - PII_REVEAL 이 있으면 "보기" → GET /auth/reveal-grant → 없으면 비밀번호 재확인(POST) → 그 한 건의 …/pii 조회.
 * - 원문은 누른 자리(컴포넌트 인스턴스)에만 보인다. 같은 레코드 응답은 허가 안에서만 공유한다.
 * - 허가 만료·끝내기·로그아웃·화면 이동(해제) 때 원문과 캐시를 버린다. 늦게 온 응답은 세대(gen)로 걸러 다시 보이지 않게 한다.
 */
type Grant = { active: boolean; expiresAt: string | null };
type Source = {
  /** 한 건 원문 경로 (…/pii). 같은 경로는 허가 안에서 한 번만 부른다 (이름·이메일을 따로 눌러도 감사 기록 1건). */
  url: string;
  /** 응답에서 이 칸의 원문을 꺼낸다. */
  pick: (data: Record<string, unknown>) => unknown;
};

type GrantState = {
  /** 서버 expiresAt (ms). null 이면 허가 없음. */
  expiresAt: number | null;
  /** 허가가 바뀔 때마다 올린다 (만료·끝내기·새 허가). 이전 세대의 원문·응답은 버린다. */
  gen: number;
  cache: Map<string, Promise<Record<string, unknown>>>;
  set: (expiresAt: string | null) => void;
  clear: () => void;
};

let expiryTimer: number | undefined;
const useGrant = create<GrantState>((set, get) => ({
  expiresAt: null,
  gen: 0,
  cache: new Map(),
  set: (iso) => {
    const at = iso ? new Date(iso).getTime() : NaN;
    // 서버가 준 만료 시각이 없거나 이미 지났으면 허가로 보지 않는다 (임의의 시간으로 대신하지 않음)
    if (!Number.isFinite(at) || at <= Date.now()) return get().clear();
    window.clearTimeout(expiryTimer);
    expiryTimer = window.setTimeout(() => get().clear(), at - Date.now());
    set((s) => (s.expiresAt === at ? s : { expiresAt: at, gen: s.gen + 1, cache: new Map() }));
  },
  clear: () => {
    window.clearTimeout(expiryTimer);
    set((s) => ({ expiresAt: null, gen: s.gen + 1, cache: new Map() }));
  },
}));

// 로그아웃·계정 교체 때 허가 상태를 버린다
let lastAccount: string | undefined;
useAuth.subscribe((s) => {
  const id = s.me?.accountId;
  if (id !== lastAccount) {
    lastAccount = id;
    useGrant.getState().clear();
  }
});

const grantValid = () => {
  const at = useGrant.getState().expiresAt;
  return at !== null && at > Date.now();
};

/** 한 건 원문 조회. 같은 세대(허가) 안에서는 같은 경로를 한 번만 부른다. */
function readPii(url: string): Promise<Record<string, unknown>> {
  const { cache } = useGrant.getState();
  let p = cache.get(url);
  if (!p) {
    p = api.get<Record<string, unknown>>(url);
    cache.set(url, p);
    p.catch(() => cache.delete(url));
  }
  return p;
}

export function MaskedValue({ value, source, fallback = "-", label = "개인정보" }: { value: string | null | undefined; source?: Source; fallback?: ReactNode; label?: string }) {
  const me = useAuth((s) => s.me);
  const gen = useGrant((s) => s.gen);
  const [raw, setRaw] = useState<string | null>(null);
  const [rawGen, setRawGen] = useState(-1);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [pwStatus, setPwStatus] = useState<RevealPasswordStatus>("idle");
  const [pwError, setPwError] = useState<string | undefined>();
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const canReveal = !!source && !!me && can(me, "PII_REVEAL");
  // 허가가 바뀌었으면(만료·끝내기·새 허가) 이 자리의 원문은 버린다
  const shown = canReveal && raw !== null && rawGen === gen ? raw : null;

  const load = async () => {
    const g = useGrant.getState().gen;
    try {
      const data = await readPii(source!.url);
      if (!alive.current || useGrant.getState().gen !== g || !grantValid()) return; // 늦게 온 응답
      const v = source!.pick(data);
      setRaw(v === null || v === undefined ? "" : String(v));
      setRawGen(g);
      setError(undefined);
    } catch (e) {
      if (e instanceof ApiError && e.code === "PII_GRANT_REQUIRED") {
        useGrant.getState().clear();
        if (alive.current) openPassword();
      } else if (alive.current) setError(errorMessage(e)); // 인증 뒤 조회 실패는 비밀번호 오류로 바꾸지 않는다
    }
  };

  const openPassword = () => {
    setPwStatus("idle");
    setPwError(undefined);
    setAsking(true);
  };

  const reveal = async () => {
    if (!source) return;
    setError(undefined);
    setBusy(true);
    try {
      if (!grantValid()) {
        const g = await api.get<Grant>("/auth/reveal-grant");
        if (g.active) useGrant.getState().set(g.expiresAt);
      }
      if (!grantValid()) {
        if (alive.current) openPassword();
        return;
      }
      await load();
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const confirm = async (password: string) => {
    setPwStatus("checking");
    setPwError(undefined);
    try {
      const g = (await request<Grant>("POST", "/auth/reveal-grant", { password }, { renew: false })).result;
      useGrant.getState().set(g.expiresAt);
      if (!grantValid()) throw new ApiError(500, "허가 시간을 받지 못했습니다. 다시 시도하세요.");
      if (!alive.current) return;
      setAsking(false);
      setBusy(true);
      await load();
      if (alive.current) setBusy(false);
    } catch (e) {
      if (!alive.current) return;
      const status = e instanceof ApiError ? e.status : 0;
      setPwStatus(status === 401 ? "incorrect" : status === 429 ? "rate-limited" : status === 403 ? "forbidden" : "idle");
      setPwError(errorMessage(e));
    }
  };

  return (
    <>
      <MaskedValueView
        value={value}
        label={label}
        revealedValue={shown}
        canReveal={canReveal}
        onReveal={() => void reveal()}
        onHide={() => setRaw(null)}
        busy={busy}
        error={error}
        fallback={fallback}
      />
      <RevealPasswordDialog
        open={asking}
        onOpenChange={(v) => {
          if (!v) setAsking(false);
        }}
        onConfirm={confirm}
        status={pwStatus}
        error={pwError}
      />
    </>
  );
}

/** 본문 위 작은 상태줄: 허가 중 · 남은 시간 · 끝내기 (시안 RevealGrantStatus). 허가가 없으면 안 보인다. */
export function PiiGrantBar() {
  const expiresAt = useGrant((s) => s.expiresAt);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    if (!expiresAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return (
    <RevealGrantStatus
      remainingSeconds={(expiresAt - now) / 1000}
      busy={busy}
      error={error}
      onEnd={() => {
        setBusy(true);
        setError(undefined);
        api
          .del("/auth/reveal-grant")
          .then(() => useGrant.getState().clear())
          .catch((e) => setError(errorMessage(e)))
          .finally(() => setBusy(false));
      }}
    />
  );
}
